import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ResponsiveContext, Button, Layer, Text, TextArea, Anchor, TextInput, Box } from 'grommet';
import { Close, AddCircle, FormClose } from 'grommet-icons';

import { Placeholder, ErrorForm } from '../../../../components';
import { TitleField, SelectSearch, DescriptionField } from '../../../../components/form';
import { TitleDetail } from '../../../../components/detail';

import { searchLegislativeProcesses } from '../../actions/form-search';
import { update, create, hideFormManagerLayer } from '../../actions/form-manager';
import { changeValues } from '../../../../actions/form';
import { getAuthorsOptions } from '../../../../actions/select-options';

import visibleWidth from '../../../../constants/layer-width';

import selectYesNoOptions from '../../../../constants/select-yesno-options';

function FormCreate(props) {
  const [name, setName] = useState('');

  const {
    documentTypeOptions,
    authorsOptions,
    statusLegislativeProcessOptions,
    departmentOptions,

    id,
    menu,
    number,
    proceeding,
    status,
    areaInterest,
    type,
    year,
    authors,
    region,
    getDataFormEditError,
    isEditing,
    toggleLayer,
    loadingPostForm,
    postDataError,

    changeValues,
    hideFormManagerLayer,
  } = props;


  const callbackPostSuccess = useCallback(() => {
    const {
      searchLegislativeProcesses,
      hideFormManagerLayer,
    } = props;

    searchLegislativeProcesses();
    hideFormManagerLayer();
  }, [props]);


  const handlePost = useCallback((formParams) => {
    const { create, update } = props;

    if(formParams.id) {
      update({ formParams });
    }
    else {
      create({
        formParams,
        callbackSuccess: callbackPostSuccess
      });
    }
  }, [callbackPostSuccess, props]);


  const onSearchAuthors = useCallback((text) => {
    if (text.length < 2) return;

    const { getAuthorsOptions } = props;
    getAuthorsOptions({ name: text });
  }, [props]);


  if(toggleLayer && toggleLayer === 'manager') {
    return (
      <ResponsiveContext.Consumer>
        {size => (
          <Layer
            responsive={false}
            full='vertical'
            position='left'
            onClickOutside={hideFormManagerLayer}
            onEsc={hideFormManagerLayer}
          >
            <Box
              fill='vertical'
              width={visibleWidth[size]}
              pad={{ top: 'large', bottom: 'xsmall' }}
              overflow='auto'
              flex
            >
              {isEditing && !id && !getDataFormEditError ? (
                <Placeholder
                  title='Carregando...'
                  message='Aguarde que estamos preparando as coisas :)'
                />
              ) : null}

              {getDataFormEditError ? (
                <Placeholder
                  title='Ops! Algo deu errado :('
                  message='Estamos constrangidos por isso.'
                  button={{
                    label: 'Fechar',
                    action: hideFormManagerLayer,
                  }}
                />
              ) : null}

              {!getDataFormEditError && ((isEditing && id) || !isEditing) ? (
                <>
                  <Box
                    direction='row'
                    justify='between'
                    align='baseline'
                    margin={{ horizontal: 'large' }}
                  >
                    <TitleDetail value={id ? 'Editar dados do documento' : 'Novo documento'} />

                    <Button
                      plain
                      title='Fechar'
                      icon={<FormClose />}
                      onClick={hideFormManagerLayer}
                    />
                  </Box>


                  <Box flex overflow='auto'>
                    {/* Tipo */}
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      direction={ size === 'small' ? 'column' : 'row' }
                      align='stretch'
                      flex={{ shrink: 0 }}
                      gap='small'
                    >
                      <Box
                        width={ size === 'small' ? '100vw' : '40vw' }
                        gap='xxsmall'
                      >
                        <TitleField text='Tipo' required />

                        <DescriptionField
                          text='Selecione o tipo do documento. Requerimentos, indicações, etc.'
                        />
                      </Box>

                      <Box
                        width={ size === 'small' ? '100vw' : '50vw' }
                      >
                        <SelectSearch
                          options={documentTypeOptions}
                          value={type ?? ''}
                          labelKey='name'
                          valueKey={{ key: 'id', reduce: true }}
                          emptySearchMessage='Selecione uma opção'
                          onChange={({ value: nextValue }) =>
                            changeValues({ fieldName: 'type', value: nextValue }, 'changeFormManagerField')
                          }
                        />

                        <ErrorForm errorsList={postDataError.errors} fieldName='type' />
                      </Box>
                    </Box>


                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      flex={{ shrink: 0 }}
                    >
                      <TitleField text='Status' required />

                      <SelectSearch
                        options={statusLegislativeProcessOptions}
                        value={status ?? ''}
                        labelKey='name'
                        valueKey={{ key: 'id', reduce: true }}
                        emptySearchMessage='Selecione uma opção'
                        onChange={({ value: nextValue }) =>
                          changeValues({ fieldName: 'status', value: nextValue }, 'changeFormManagerField')
                        }
                      />

                      <ErrorForm errorsList={postDataError.errors} fieldName='status' />
                    </Box>


                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      flex={{ shrink: 0 }}
                    >
                      <TitleField text='Área de interesse' />

                      <SelectSearch
                        options={departmentOptions}
                        value={areaInterest ?? ''}
                        labelKey='name'
                        valueKey={{ key: 'id', reduce: true }}
                        emptySearchMessage='Selecione uma opção'
                        onChange={({ value: nextValue }) =>
                          changeValues({ fieldName: 'areaInterest', value: nextValue }, 'changeFormManagerField')
                        }
                      />
                    </Box>

                    <Box
                      border={{ color: 'light-3', side: 'bottom' }}
                      margin={{ top: 'medium', bottom: 'medium' }}
                    />


                    {/* Número / Ano */}
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      direction={ size === 'small' ? 'column' : 'row' }
                      align='stretch'
                      flex={{ shrink: 0 }}
                      gap='small'
                    >
                      <Box
                        width={ size === 'small' ? '100vw' : '40vw' }
                        gap='xxsmall'
                      >
                        <TitleField
                          text='Número e ano' required
                        />

                        <DescriptionField text='Número e ano do documento.' />
                      </Box>

                      <Box
                        width={ size === 'small' ? '100vw' : '50vw' }
                        direction='row'
                        gap='small'
                      >
                        <Box
                          fill='horizontal'
                        >
                          <TextInput
                            type='number'
                            min={0}
                            value={number ?? ''}
                            placeholder='Número'
                            onChange={event =>
                              changeValues({ fieldName: 'number', value: event.target.value }, 'changeFormManagerField')
                            }
                          />

                          <ErrorForm errorsList={postDataError.errors} fieldName='number' />
                        </Box>

                        <Box
                          fill='horizontal'
                        >
                          <TextInput
                            type='number'
                            min={0}
                            value={year ?? ''}
                            placeholder='Ano'
                            onChange={event =>
                              changeValues({ fieldName: 'year', value: event.target.value }, 'changeFormManagerField')
                            }
                          />

                          <ErrorForm errorsList={postDataError.errors} fieldName='year' />
                        </Box>
                      </Box>
                    </Box>


                    {/* Em tramitação */}
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      direction={ size === 'small' ? 'column' : 'row' }
                      align='stretch'
                      flex={{ shrink: 0 }}
                      gap='small'
                    >
                      <Box
                        width={ size === 'small' ? '100vw' : '40vw' }
                        gap='xxsmall'
                      >
                        <TitleField
                          text='Em tramitação'
                        />

                        <DescriptionField
                          text='O documento está em tramitação ou já foi concluído?'
                        />
                      </Box>

                      <Box
                        width={ size === 'small' ? '100vw' : '50vw' }
                      >
                        <SelectSearch
                          options={selectYesNoOptions}
                          value={proceeding ?? ''}
                          labelKey='name'
                          valueKey={{ key: 'id', reduce: true }}
                          emptySearchMessage='Selecione uma opção'
                          onChange={({ value: nextValue }) =>
                            changeValues({ fieldName: 'proceeding', value: nextValue }, 'changeFormManagerField')
                          }
                        />
                      </Box>
                    </Box>

                    <Box
                      border={{ color: 'light-3', side: 'bottom' }}
                      margin={{ top: 'medium', bottom: 'medium' }}
                    />


                    {/* Região */}
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      flex={{ shrink: 0 }}
                    >
                      <TitleField text='Região (bairro, cidade)' />

                      <TextInput
                        maxLength={50}
                        value={region ?? ''}
                        onChange={event =>
                          changeValues({ fieldName: 'region', value: event.target.value }, 'changeFormManagerField')
                        }
                      />

                      <ErrorForm errorsList={postDataError.errors} fieldName='region' />
                    </Box>

                    <Box
                      border={{ color: 'light-3', side: 'bottom' }}
                      margin={{ top: 'medium', bottom: 'medium' }}
                    />


                    {/* Authors */}
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      direction={ size === 'small' ? 'column' : 'row' }
                      align='stretch'
                      flex={{ shrink: 0 }}
                      gap='small'
                    >
                      <Box
                        width={ size === 'small' ? '100vw' : '40vw' }
                        gap='xxsmall'
                      >
                        <TitleField
                          text='Autor(es)'
                        />

                        <DescriptionField
                          text='Você pode informar um ou mais nomes...'
                        />
                      </Box>


                      <Box
                        width={ size === 'small' ? '100vw' : '60vw' }
                      >
                        <Box
                          direction='row'
                          round='xsmall'
                          border={{ color: 'light-4' }}
                          background='white'
                        >
                          <TextInput
                            plain
                            maxLength={50}
                            placeholder='Informe o nome e clique em adicionar.'
                            onChange={event => {
                              const { value: newValue } = event.target;

                              setName(newValue);
                              onSearchAuthors(newValue);
                            }}
                            value={name}
                            suggestions={
                              authorsOptions.map(({ name }) => ({
                                label: (
                                  <Box
                                    pad='small'
                                  >
                                    <Text size='small' color='dark-3'>{name}</Text>
                                  </Box>
                                ),
                                value: { name }
                              }))
                            }
                            onSuggestionSelect={event => {
                              const { name } = event.suggestion.value;
                              setName(name);
                            }}
                          />

                          <Button
                            icon={<AddCircle color='neutral-1' />}
                            disabled={!name}
                            onClick={() => {
                              changeValues(
                                { ac: 'add', fieldName: 'authors', value: name }, 'addOrRemoveinList'
                              );

                              setName('');
                            }}
                          />
                        </Box>

                        <ErrorForm errorsList={postDataError.errors} fieldName='options' />
                      </Box>
                    </Box>


                    {/* Autores lista */}
                    {authors && authors.length > 0 ? (
                      <Box
                        margin={{
                          vertical: 'small',
                          horizontal: 'large'
                        }}
                        flex={{ shrink: 0 }}
                        border={{ color: 'light-4' }}
                        round='small'
                      >
                        {authors.map((item, index) => (
                          <Box
                            key={index}
                            align='center'
                            direction='row'
                            pad={{ horizontal: 'small' }}
                            justify='between'
                          >
                            <Text size='small' color='dark-5'>{item}</Text>

                            <Anchor
                              icon={<Close size='small' />}
                              color='status-critical'
                              title='Remover'
                              onClick={() =>
                                changeValues(
                                  { ac: 'delete', fieldName: 'authors', value: item }, 'addOrRemoveinList'
                                )
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : null}

                    <Box
                      border={{ color: 'light-3', side: 'bottom' }}
                      margin={{ top: 'medium', bottom: 'medium' }}
                    />


                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      flex={{ shrink: 0 }}
                    >
                      <TitleField text='Assunto' required />

                      <TextArea
                        rows={5}
                        resize='vertical'
                        maxLength={2000}
                        value={menu ?? ''}
                        onChange={event =>
                          changeValues({ fieldName: 'menu', value: event.target.value }, 'changeFormManagerField')
                        }
                      />

                      <ErrorForm errorsList={postDataError.errors} fieldName='menu' />
                    </Box>
                  </Box>


                  <Box
                    direction='row'
                    pad={{ vertical: 'medium' }}
                    justify='center'
                    align='center'
                    gap='small'
                  >
                    <Box width='xsmall'>
                      <Anchor
                        color='dark-3'
                        label='Cancelar'
                        disabled={ loadingPostForm }
                        onClick={hideFormManagerLayer}
                      />
                    </Box>

                    <Box width='small'>
                      <Button
                        color='brand'
                        primary
                        label={ !loadingPostForm ? 'Salvar': 'Salvando...' }
                        disabled={ loadingPostForm }
                        onClick={() =>
                          handlePost({
                            id,
                            menu,
                            number,
                            proceeding,
                            status,
                            areaInterest,
                            type,
                            year,
                            authors,
                            region,
                          })
                        }
                      />
                    </Box>
                  </Box>
                </>
              ) : null}
            </Box>
          </Layer>
        )}
      </ResponsiveContext.Consumer>
    )
  }

  return null;
}

const mapStateToProps = ({ legislativeProcessesReducer: { formManager }, selectOptionsReducer, layerReducer }) => ({
  documentTypeOptions: selectOptionsReducer.documentTypeOptions,
  authorsOptions: selectOptionsReducer.authorsOptions,
  statusLegislativeProcessOptions: selectOptionsReducer.statusLegislativeProcessOptions,
  departmentOptions: selectOptionsReducer.departmentOptions,

  id: formManager.id,
  menu: formManager.menu,
  number: formManager.number,
  proceeding: formManager.proceeding,
  status: formManager.status,
  areaInterest: formManager.areaInterest,
  type: formManager.type,
  year: formManager.year,
  authors: formManager.authors,
  region: formManager.region,

  isEditing: formManager.isEditing,
  getDataFormEditError: formManager.getDataFormEditError,

  loadingPostForm: formManager.loadingPostForm,
  postDataError: formManager.postDataError,

  toggleLayer: layerReducer.toggleLayer,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    getAuthorsOptions,
    update,
    create,
    changeValues,
    searchLegislativeProcesses,
    hideFormManagerLayer,
  }, dispatch);

export const Create = connect(mapStateToProps, mapDispatchToProps)(FormCreate);
